/**
 * WorkshopDetailPage — the workshop detail home (BON-117 foundation). Mounted
 * at `/admin/workshops/:workshopId` and `/me/workshops/:workshopId`, so it
 * reads the workshop id from the route and its mode from the address (teacher
 * mode is read-only). The loaded body, the two-column grid, and the state
 * framing each live in their own file under `detail/` (react/no-multi-comp);
 * this module wires the load + route params.
 */
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { chipDetailUrlFor } from '../workshopDetailUrlFor';

import { StateShell } from './detail/StateShell';
import { useWorkshopReadOnly } from './detail/use-workshop-read-only';
import { WorkshopDetailBody } from './detail/WorkshopDetailBody';
import { useWorkshopViewMode } from './detail/workshopDetailContext';
import { useWorkshop } from './useWorkshop';

/** BON-239: while the nested check-in route is open, poll the detail so
 *  several crew checking in the same workshop see each other's check-ins
 *  near-live. Off otherwise — the detail stays a one-shot read. */
const CHECK_IN_POLL_MS = 4000;

export default function WorkshopDetailPage() {
  const { workshopId = '' } = useParams<{ workshopId: string }>();
  const mode = useWorkshopViewMode();

  const location = useLocation();
  const checkInOpen = location.pathname.endsWith('/checkin');
  const {
    data: workshop,
    isLoading,
    error,
  } = useWorkshop(workshopId, checkInOpen ? { pollMs: CHECK_IN_POLL_MS } : undefined);
  // Read-only needs the workshop's region, so it is resolved after the load
  // rather than from the address alone.
  const readOnly = useWorkshopReadOnly(workshop?.venueRegionId);

  if (isLoading) {
    return <div style={{ padding: 24, color: 'var(--nb-subtle)' }}>Loading workshop…</div>;
  }
  if (error || !workshop) {
    return (
      <StateShell>{error instanceof Error ? error.message : 'Workshop not found.'}</StateShell>
    );
  }
  if (workshop.isPrivate) {
    return <Navigate replace to={chipDetailUrlFor(mode, workshop)} state={location.state} />;
  }
  return <WorkshopDetailBody workshop={workshop} shell={mode} readOnly={readOnly} />;
}
